import { createStore, setStateHelper } from "@neonumy/ant-design";
import { Breakpoint } from "antd/lib/_util/responsiveObserve";
import { ID } from "types/common";
import { StoreApi, UseBoundStore } from "zustand";
import { BulkType, CompanyType, DocumentFullInfo } from "../types/bulkStore";
import { ListBulksAssignedAtSameTime } from "queries/neodocs/neodocs";

const initialState = {
  company: undefined,
  documentsList: [],
  selectedDocument: undefined,
  id: undefined,
  assignTo: undefined,
  documentsTotal: undefined,
  documentsTotalRegistered: undefined,
  documentsTotalValidated: undefined,
  endedAt: undefined,
  validateAssignTo: undefined,
  validateEndedAt: undefined,
  loadingPage: false, //savingForm
  loadingDocuments: false,
  disableFormButtons: false, //savingData
  refetchQueries: false,
  screens: {},
  showPreview: true,
  listBulksAssignedAtSameTime: [],
};

export interface BulkStore {
  company: CompanyType;
  documentsList: DocumentFullInfo[];
  selectedDocument: DocumentFullInfo;
  id: ID;
  assignTo: {
    id: ID;
  };
  documentsTotal: number;
  documentsTotalRegistered: number;
  documentsTotalValidated: number;
  endedAt: string;
  validateAssignTo: {
    id: ID;
  };
  validateEndedAt: string;
  loadingPage: boolean;
  loadingDocuments: boolean;
  disableFormButtons: boolean;
  refetchQueries: boolean;
  screens: Partial<Record<Breakpoint, boolean>>;
  showPreview: boolean;
  bulkOwnerChanged: boolean;
  listBulksAssignedAtSameTime: ListBulksAssignedAtSameTime[];
  useNeofrais: boolean;
  provisionalBook: ID;
  provisionalCycle: ID;
  resetStore: () => void;
  setSelectedDocument: (selectedDocument: DocumentFullInfo) => void;
  setDocumentsList: (documentsList: DocumentFullInfo[]) => void;
  unValidateDocument: (currentIndex: number) => void;
  checkDuplicated: ({
    duplicated,
    currentIndex,
  }: {
    duplicated: boolean;
    currentIndex: number;
  }) => void;
  updateDocumentBook: ({
    bookInfo,
    currentIndex,
  }: {
    bookInfo: {
      bookId: ID;
      bookTypeSlug: string;
    };
    currentIndex: number;
  }) => void;
  updateExtractedJsonUrl: ({
    documentID,
    extractedJsonUrl,
  }: {
    documentID: ID;
    extractedJsonUrl: string;
  }) => void;
  setCompany: (company: CompanyType) => void;
  setBulk: (bulk: BulkType) => void;
  decrementDocumentsTotalValidated: () => void;
  decrementDocumentsTotalRegistered: () => void;
  finishDocumentsTotalRegistered: () => void;
  finishDocumentsTotalValidated: () => void;
  setLoadingPage: (loadingPage: boolean) => void;
  setLoadingDocuments: (loadingDocuments: boolean) => void;
  setDisablePageButtons: (disableFormButtons: boolean) => void;
  setRefetchQueries: (refetchQueries: boolean) => void;
  setScreens: (screens: Partial<Record<Breakpoint, boolean>>) => void;
  setShowPreview: (showPreview: boolean) => void;
  setBulkOwnerChanged: (bulkOwnerChanged: boolean) => void;
  setProvisionalBook: (provisionalBook: ID) => void;
  setProvisionalCycle: (provisionalCycle: ID) => void;
  setListBulksAssignedAtSameTime: (
    listBulksAssignedAtSameTime: ListBulksAssignedAtSameTime[],
  ) => void;
}

const useBulkStore: UseBoundStore<StoreApi<BulkStore>> = createStore<BulkStore>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (set: (fn: (draft: Record<string, any>) => void) => void) => ({
    ...initialState,
    resetStore: () => {
      set(() => ({
        ...initialState,
      }));
    },
    setSelectedDocument: (selectedDocument: DocumentFullInfo) => {
      setStateHelper("selectedDocument", selectedDocument, set);
    },
    setDocumentsList: (documentsList: DocumentFullInfo[]) => {
      setStateHelper("documentsList", documentsList, set);
    },
    unValidateDocument: (currentIndex: number) => {
      set(state => {
        const updateListDocuments = [...state.documentsList].map(({ ...item }, index: number) => {
          if (index === currentIndex) {
            item.validatedAt = null;
          }
          return item;
        });
        return {
          documentsList: updateListDocuments,
        };
      });
    },
    checkDuplicated: ({
      duplicated,
      currentIndex,
    }: {
      duplicated: boolean;
      currentIndex: number;
    }) => {
      set(state => {
        const listDocument = [...state.documentsList].map(({ ...item }, index) => {
          if (index === currentIndex) {
            item.isDuplicated = duplicated;
          }
          return item;
        });
        return {
          documentsList: listDocument,
        };
      });
    },
    updateDocumentBook: ({
      bookInfo,
      currentIndex,
    }: {
      bookInfo: {
        bookId: ID;
        bookTypeSlug: string;
      };
      currentIndex: number;
    }) => {
      set(state => {
        const documentsListUpdated = [...state.documentsList].map(({ ...item }, index) => {
          if (index === currentIndex) {
            return {
              ...item,
              ...bookInfo,
            };
          }
          return item;
        });
        return {
          documentsList: documentsListUpdated,
        };
      });
    },
    updateExtractedJsonUrl: ({
      documentID,
      extractedJsonUrl,
    }: {
      documentID: ID;
      extractedJsonUrl: string;
    }) => {
      set(state => {
        const documentsListUpdated = [...state.documentsList].map(({ ...item }) => {
          if (item.id === documentID) {
            return {
              ...item,
              extractedJsonUrl,
            };
          }
          return item;
        });
        return {
          documentsList: documentsListUpdated,
        };
      });
    },
    setCompany: (company: CompanyType) => {
      // The API returns analyticalLevel as a string, but it is compared with === against numbers.
      setStateHelper(
        "company",
        { ...company, analyticalLevel: Number(company.analyticalLevel) },
        set,
      );
    },
    setBulk: (bulk: BulkType) => {
      set(() => ({ ...bulk }));
    },
    decrementDocumentsTotalValidated: () => {
      set(state => ({
        documentsTotalValidated: state.documentsTotalValidated - 1,
      }));
    },
    decrementDocumentsTotalRegistered: () => {
      set(state => ({
        documentsTotalRegistered: state.documentsTotalRegistered - 1,
      }));
    },
    finishDocumentsTotalRegistered: () => {
      set(state => ({
        documentsTotalRegistered: state.documentsTotal,
      }));
    },
    finishDocumentsTotalValidated: () => {
      set(state => ({
        documentsTotalValidated: state.documentsTotal,
      }));
    },
    setLoadingPage: (loadingPage: boolean) => {
      setStateHelper("loadingPage", loadingPage, set);
    },
    setLoadingDocuments: (loadingDocuments: boolean) => {
      setStateHelper("loadingDocuments", loadingDocuments, set);
    },
    setDisablePageButtons: (disableFormButtons: boolean) => {
      setStateHelper("disableFormButtons", disableFormButtons, set);
    },
    setRefetchQueries: (refetchQueries: boolean) => {
      setStateHelper("refetchQueries", refetchQueries, set);
    },
    setScreens: (screens: Partial<Record<Breakpoint, boolean>>) => {
      setStateHelper("screens", screens, set);
    },
    setShowPreview: (showPreview: boolean) => {
      setStateHelper("showPreview", showPreview, set);
    },
    setBulkOwnerChanged: (bulkOwnerChanged: boolean) => {
      setStateHelper("bulkOwnerChanged", bulkOwnerChanged, set);
    },
    setProvisionalBook: (provisionalBook: boolean) => {
      setStateHelper("provisionalBook", provisionalBook, set);
    },
    setProvisionalCycle: (provisionalCycle: boolean) => {
      setStateHelper("provisionalCycle", provisionalCycle, set);
    },
    setListBulksAssignedAtSameTime: (
      listBulksAssignedAtSameTime: ListBulksAssignedAtSameTime[],
    ) => {
      setStateHelper("listBulksAssignedAtSameTime", listBulksAssignedAtSameTime, set);
    },
  }),
);

export default useBulkStore;
